import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, Trash2, ExternalLink, Mail, LogOut, ChevronLeft, ChevronRight, X as XIcon } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  listApplications, getApplication, updateApplicationStatus, deleteApplication, getStats,
  getUsername, clearSession,
} from "@/lib/apiClient";
import AdminCrm from "@/components/AdminCrm";
import AdminVerificationVideo from "@/components/AdminVerificationVideo";

const STATUSES = ["new", "reviewing", "shortlisted", "signed", "declined"];

const statusColor = {
  new: "bg-[#000000] text-white",
  reviewing: "bg-amber-500 text-black",
  shortlisted: "bg-blue-600 text-white",
  signed: "bg-emerald-600 text-white",
  declined: "bg-neutral-400 text-white",
};

export default function Admin() {
  const navigate = useNavigate();
  const currentUser = getUsername();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { images: [], index: 0 }

  const openLightbox = (src, images = null, index = 0) => {
    const list = images && images.length ? images : [src];
    const idx = images ? index : 0;
    setLightbox({ images: list, index: idx });
  };
  const closeLightbox = () => setLightbox(null);
  const shiftLightbox = (delta) => {
    setLightbox((lb) => lb && ({
      ...lb,
      index: (lb.index + delta + lb.images.length) % lb.images.length,
    }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        listApplications(filter === "all" ? undefined : filter),
        getStats(),
      ]);
      setApps(a);
      setStats(s);
    } catch {
      toast.error("Failed to load applications.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const data = await getApplication(id);
      setSelected(data);
    } catch {
      toast.error("Could not load application.");
    } finally {
      setDetailLoading(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateApplicationStatus(id, status);
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      if (selected?.id === id) setSelected(updated);
      getStats().then(setStats).catch(() => {});
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await deleteApplication(id);
      setApps((prev) => prev.filter((a) => a.id !== id));
      if (selected?.id === id) setSelected(null);
      getStats().then(setStats).catch(() => {});
      toast.success("Application deleted.");
    } catch {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#ffffff] text-[#0a0a0a]">
      {/* Header */}
      <header className="border-b border-[#0a0a0a]/10 bg-[#0a0a0a] text-[#ffffff]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <Link to="/" className="font-display text-2xl font-black tracking-tighter uppercase">
              Vogue<span className="text-[#000000]">Verve</span>
            </Link>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffffff]/50 mt-1">Scouting Console</p>
          </div>
          <div className="flex items-center gap-5">
            {currentUser && (
              <span data-testid="admin-current-user" className="hidden md:inline text-[10px] uppercase tracking-[0.25em] font-bold text-white/50">
                Signed in · <span className="text-white/80">{currentUser}</span>
              </span>
            )}
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#ffffff]/70 hover:text-[#ffffff] transition-colors"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <Link to="/" className="text-xs uppercase tracking-[0.2em] font-bold text-[#ffffff]/70 hover:text-[#ffffff]">
              View Site
            </Link>
            <button
              data-testid="admin-signout-btn"
              onClick={() => {
                clearSession();
                toast.success("Signed out.");
                navigate("/login", { replace: true });
              }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white/70 hover:text-white transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-[#0a0a0a]/10 border border-[#0a0a0a]/10 mb-10">
          <StatCard label="Total" value={stats.total ?? 0} accent />
          {STATUSES.map((s) => (
            <StatCard key={s} label={s} value={stats[s] ?? 0} />
          ))}
        </div>

        {/* Filter */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold border transition-colors ${
                filter === s
                  ? "bg-[#0a0a0a] text-[#ffffff] border-[#0a0a0a]"
                  : "bg-transparent text-[#0a0a0a]/60 border-[#0a0a0a]/20 hover:border-[#0a0a0a]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-[#0a0a0a]/10">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#0a0a0a]/50">
              <Loader2 className="animate-spin mr-2" size={20} /> Loading applications...
            </div>
          ) : apps.length === 0 ? (
            <div data-testid="admin-empty" className="text-center py-24 text-[#0a0a0a]/50">
              <p className="font-display text-2xl mb-2">No applications yet</p>
              <p className="text-sm">New submissions from the site will appear here.</p>
            </div>
          ) : (
            <Table data-testid="admin-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase text-xs tracking-[0.15em]">Name</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.15em] hidden md:table-cell">Location</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.15em] hidden lg:table-cell">Experience</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.15em] hidden md:table-cell">Photos</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.15em]">Status</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.15em] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((a) => (
                  <TableRow key={a.id} data-testid={`admin-row-${a.id}`} className="cursor-pointer hover:bg-[#ffffff]" onClick={() => openDetail(a.id)}>
                    <TableCell>
                      <div className="font-semibold">{a.full_name}</div>
                      <div className="text-xs text-[#0a0a0a]/50">{a.email}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-[#0a0a0a]/70">
                      {[a.city, a.country].filter(Boolean).join(", ") || "—"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-[#0a0a0a]/70 capitalize">{a.experience || "—"}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{a.photo_count}</TableCell>
                    <TableCell>
                      <Badge className={`${statusColor[a.status]} uppercase text-[10px] tracking-wider rounded-sm`}>{a.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        data-testid={`delete-${a.id}`}
                        onClick={() => remove(a.id)}
                        className="text-[#0a0a0a]/40 hover:text-[#000000] transition-colors p-2"
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-lenis-prevent data-testid="admin-detail-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm">
          {detailLoading || !selected ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" /></div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl font-light tracking-tight">
                  {selected.full_name}
                </DialogTitle>
                <DialogDescription className="text-xs uppercase tracking-[0.2em] text-[#0a0a0a]/50">
                  Application review
                </DialogDescription>
              </DialogHeader>

              <div className="flex items-center gap-3 mb-4">
                <Badge className={`${statusColor[selected.status]} uppercase text-[10px] tracking-wider rounded-sm`}>{selected.status}</Badge>
                <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v)}>
                  <SelectTrigger data-testid="detail-status-select" className="w-44 h-8 text-xs rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`set-status-${s}`} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selected.photos?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
                  {selected.photos.map((p, i) => (
                    <button
                      key={`${p.slice(-24)}-${i}`}
                      type="button"
                      data-testid={`admin-photo-${i}`}
                      onClick={() => openLightbox(p, selected.photos, i)}
                      className="aspect-[3/4] overflow-hidden bg-[#0a0a0a]/5"
                    >
                      <img src={p} alt={`${selected.full_name} ${i}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </button>
                  ))}
                </div>
              )}

              {(selected.verification_video_id || selected.verification_video || selected.verification_selfie) && (
                <div className="mb-6 border border-[#0a0a0a]/10 bg-[#fafafa] p-4 rounded-sm" data-testid="admin-verification-block">
                  <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0a0a0a]/50">
                        {(selected.verification_video_id || selected.verification_video) ? "Applicant verification video" : "Live verification selfie"}
                      </span>
                    </div>
                    {selected.verification_code && (
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0a0a0a]/40">
                          Challenge code shown
                        </p>
                        <p data-testid="admin-verification-code" className="font-body text-xl font-black tabular-nums tracking-widest">{selected.verification_code}</p>
                      </div>
                    )}
                  </div>
                  {(selected.verification_video_id || selected.verification_video) ? (
                    <AdminVerificationVideo applicationId={selected.id} referencePhoto={selected.photos?.[0]} />
                  ) : (
                    <button
                      type="button"
                      data-testid="admin-verification-photo"
                      onClick={() => openLightbox(selected.verification_selfie)}
                      className="block w-40 h-40 overflow-hidden bg-black rounded-sm"
                    >
                      <img
                        src={selected.verification_selfie}
                        alt="verification selfie"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <Detail label="Email" value={<a className="text-[#000000] hover:underline inline-flex items-center gap-1" href={`mailto:${selected.email}`}><Mail size={13} />{selected.email}</a>} />
                <Detail label="Phone" value={selected.phone || "—"} />
                <Detail label="Age" value={selected.age || "—"} />
                <Detail label="Address" value={selected.address_line || "—"} />
                <Detail label="Location" value={[selected.city, selected.country].filter(Boolean).join(", ") || "—"} />
                <Detail label="Height" value={selected.height_cm ? `${selected.height_cm} cm` : "—"} />
                <Detail label="Measurements" value={selected.measurements || "—"} />
                <Detail label="Experience" value={<span className="capitalize">{selected.experience || "—"}</span>} />
                <Detail label="Instagram" value={selected.instagram || "—"} />
                <Detail label="TikTok" value={selected.tiktok || "—"} />
                <Detail label="Portfolio" value={selected.portfolio_url ? <a className="text-[#000000] hover:underline inline-flex items-center gap-1" href={selected.portfolio_url} target="_blank" rel="noreferrer">Link <ExternalLink size={12} /></a> : "—"} />
              </div>

              {selected.goals && (
                <div className="mt-6 pt-6 border-t border-[#0a0a0a]/10">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0a0a0a]/50 mb-2">Career Goals</p>
                  <p className="text-sm text-[#0a0a0a]/80 leading-relaxed">{selected.goals}</p>
                </div>
              )}

              <AdminCrm
                application={selected}
                onImageClick={openLightbox}
                onChange={(updated) => setSelected(updated)}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo lightbox */}
      {lightbox && (
        <div
          data-testid="admin-lightbox"
          onClick={closeLightbox}
          className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4"
        >
          <button
            data-testid="lightbox-close"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-6 right-6 text-white/70 hover:text-white p-2"
            aria-label="Close"
          >
            <XIcon size={28} />
          </button>
          {lightbox.images.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); shiftLightbox(-1); }}
                className="absolute left-4 md:left-8 text-white/70 hover:text-white p-2"
                aria-label="Previous"
              >
                <ChevronLeft size={36} />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); shiftLightbox(1); }}
                className="absolute right-4 md:right-8 text-white/70 hover:text-white p-2"
                aria-label="Next"
              >
                <ChevronRight size={36} />
              </button>
              <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/50 tabular-nums">
                {lightbox.index + 1} / {lightbox.images.length}
              </span>
            </>
          )}
          <img
            src={lightbox.images[lightbox.index]}
            alt="preview"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div className={`p-6 bg-white ${accent ? "border-b-2 border-[#000000]" : ""}`}>
    <div className={`font-body text-4xl font-extrabold tabular-nums ${accent ? "text-[#000000]" : "text-[#0a0a0a]"}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0a0a0a]/50 mt-2 capitalize">{label}</div>
  </div>
);

const Detail = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0a0a0a]/40 mb-1">{label}</p>
    <p className="text-[#0a0a0a]/90">{value}</p>
  </div>
);
